import { useMutation } from '@apollo/client';
import type {
  UpdateQuestionnaireTemplateMutation,
  UpdateQuestionnaireTemplateMutationVariables,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  namedOperations,
  UpdateQuestionnaireTemplateDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useUpdateQuestionnaireTemplateTRPC } from './useUpdateQuestionnaireTemplateTRPC';

export const useUpdateQuestionnaireTemplate = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation (legacy)
  const [updateQuestionnaireTemplateGraphQL, graphqlState] = useMutation(
    UpdateQuestionnaireTemplateDocument,
    {
      update: (cache) => {
        evictField(cache, 'questionnaire_template');
        evictField(cache, 'questionnaire_template_by_pk');
      },
      refetchQueries: [
        namedOperations.Query.getQuestionnaireTemplateById,
        namedOperations.Query.getQuestionnaireTemplates,
      ],
    }
  );

  // tRPC mutation
  const trpcMutation = useUpdateQuestionnaireTemplateTRPC();

  const updateQuestionnaireTemplate = async (input: {
    variables: UpdateQuestionnaireTemplateMutationVariables;
  }): Promise<UpdateQuestionnaireTemplateMutation> => {
    if (trpcEnabled) {
      const object = input.variables.object;

      return trpcMutation.updateQuestionnaireTemplate({
        Id: object?.Id ?? '',
        Title: object?.Title ?? '',
        Description: object?.Description,
        CustomAttributeData: object?.CustomAttributeData,
        OwnerUserIds: object?.OwnerUserIds ?? [],
        OwnerGroupIds: object?.OwnerGroupIds ?? [],
        ContributorUserIds: object?.ContributorUserIds ?? [],
        ContributorGroupIds: object?.ContributorGroupIds ?? [],
        ViewerUserIds: object?.ViewerUserIds,
        ViewerGroupIds: object?.ViewerGroupIds,
        TagTypeIds: object?.TagTypeIds ?? [],
        DepartmentTypeIds: object?.DepartmentTypeIds ?? [],
      });
    }

    const result = await updateQuestionnaireTemplateGraphQL({
      variables: input.variables,
    });
    if (!result.data) {
      throw new Error('Failed to update questionnaire template');
    }

    return result.data;
  };

  // Return appropriate state based on feature flag
  if (trpcEnabled) {
    return {
      updateQuestionnaireTemplate,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    updateQuestionnaireTemplate,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
